import { RouteInfoContext } from "@/components/context-providers/RouteInfoProvider";
import {
  type EntityRef,
  getEntityHref,
  getEntityKindDefinition,
} from "@/components/entity-preview/entity-kinds";
import {
  type EntityPreviewField,
  getEntityPreviewDetailsHook,
} from "@/components/entity-preview/entity-preview-details";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import { Icon, type IconName } from "@/components/icons/Icon";
import { Skeleton } from "@/components/loaders/Skeleton";
import { TooltipV2, type TooltipPlacement } from "@/components/TooltipV2";
import { cx } from "@/utils/classnames";
import React, { useContext, type ReactElement } from "react";

/** The floating surface every entity preview sits on, overriding the tooltip's compact chrome. */
const ENTITY_PREVIEW_SURFACE_CLASS =
  "!block !max-w-none !p-0 !rounded-cds-6 !text-cds-md !bg-cds-surface-default !shadow-cds-l " +
  "dark:!bg-cds-elevated-dark dark:!shadow-cds-l-dark";

export interface EntityPreviewLink {
  href: string;
  label: string;
}

export interface EntityPreviewCardProps {
  entity: EntityRef;
  /** Overrides the kind's icon, for hosts that know a more specific one. */
  icon?: IconName;
  /** Overrides the kind's generic description. */
  description?: string;
  fields?: readonly EntityPreviewField[];
  /** The reader's relation to the entity, such as when they last visited or edited it. */
  meta?: string;
  /** A link out to the entity, opened in a new tab so the reader keeps their place. */
  link?: EntityPreviewLink | null;
  className?: string;
}

/**
 * The one layout every entity preview uses: icon, name, and kind; a description and the id; a
 * list of fields; and a footer for the reader's own context and a link out. Kinds supply data
 * only, so previews read the same whichever surface or kind shows them.
 */
export const EntityPreviewCard: React.FC<EntityPreviewCardProps> = ({
  entity,
  icon,
  description,
  fields,
  meta,
  link,
  className,
}) => {
  const definition = getEntityKindDefinition(entity.kind);
  const showsId = definition.showsId && entity.id !== entity.name;
  const hasFooter = (meta != null && meta.length > 0) || link != null;

  return (
    <div
      data-ds-v2={""}
      className={cx(
        "w-[288px] max-w-full overflow-hidden text-cds-ink-medium dark:text-zinc-300",
        className,
      )}
    >
      <div className={"flex items-start justify-between gap-cds-12 px-cds-12 py-cds-8"}>
        <span
          className={
            "flex min-w-0 items-start gap-cds-6 text-cds-md text-cds-ink-strong dark:text-zinc-50"
          }
        >
          <Icon
            name={icon ?? definition.icon}
            size={"sm"}
            aria-hidden={true}
            className={"mt-[2px] shrink-0 text-cds-ink-subtle dark:text-zinc-400"}
          />
          <span className={"min-w-0 break-words font-medium"}>{entity.name}</span>
        </span>
        <span
          className={
            "shrink-0 rounded-cds-4 bg-cds-surface-active px-cds-6 py-px text-cds-xs font-medium " +
            "text-cds-ink-subtle dark:bg-white/[0.06] dark:text-zinc-400"
          }
        >
          {definition.label}
        </span>
      </div>
      <div className={"h-px bg-cds-surface-border dark:bg-white/[0.08]"} />
      <div className={"flex flex-col gap-cds-4 px-cds-12 py-cds-8"}>
        <p className={"text-cds-body-sm text-cds-ink-medium dark:text-zinc-300"}>
          {description ?? definition.description}
        </p>
        {showsId && (
          <p className={"break-all font-mono text-cds-xs text-cds-ink-subtle dark:text-zinc-400"}>
            {entity.id}
          </p>
        )}
        {fields != null && fields.length > 0 && (
          <dl className={"flex flex-col gap-cds-2 pt-cds-2"}>
            {fields.map((field) => (
              <div
                key={field.label}
                className={"flex items-center justify-between gap-cds-8 text-cds-xs"}
              >
                <dt className={"text-cds-ink-subtle dark:text-zinc-400"}>{field.label}</dt>
                <dd className={"font-medium text-cds-ink-medium dark:text-zinc-300"}>
                  {field.value ?? <Skeleton shape={"text"} tone={"subtle"} className={"w-8"} />}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      {hasFooter && (
        <>
          <div className={"h-px bg-cds-surface-border dark:bg-white/[0.08]"} />
          <div
            className={"flex items-center justify-between gap-cds-8 px-cds-12 py-cds-6 text-cds-xs"}
          >
            <span className={"min-w-0 truncate text-cds-ink-subtle dark:text-zinc-400"}>
              {meta}
            </span>
            {link != null && (
              <ChalkLink
                href={link.href}
                // A preview is a peek from mid-task context, so the entity opens in a new tab.
                openInNewTab
                className={
                  "inline-flex shrink-0 items-center gap-cds-4 font-medium hover:underline"
                }
              >
                {link.label}
              </ChalkLink>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export interface EntityPreviewProps extends Omit<EntityPreviewCardProps, "fields" | "link"> {
  /** Adds the link out to the entity's page, when its kind has one. */
  showLink?: boolean;
  /** Overrides where the link out goes, for hosts whose entity has a special destination. */
  href?: string | null;
}

const LiveEntityPreview: React.FC<EntityPreviewProps> = ({
  showLink = false,
  href: hrefOverride,
  ...props
}) => {
  // Route info is optional: hosts without it (unit tests) render the preview without a link.
  const routeInfo = useContext(RouteInfoContext);
  const useDetails = getEntityPreviewDetailsHook(props.entity.kind);
  const details = useDetails(props.entity);
  const definition = getEntityKindDefinition(props.entity.kind);
  const href = !showLink
    ? null
    : hrefOverride !== undefined
      ? hrefOverride
      : getEntityHref(props.entity, {
          projectId: routeInfo?.projectId,
          environmentId: routeInfo?.environmentId,
        });

  return (
    <EntityPreviewCard
      {...props}
      description={props.description ?? details.description}
      fields={details.fields}
      link={
        href != null && definition.linkLabel != null ? { href, label: definition.linkLabel } : null
      }
    />
  );
};

/**
 * A preview with its kind's live details, fetched only while it is mounted (that is, while its
 * hover card is open). Keyed by kind because each kind's details hook runs different queries.
 */
export const EntityPreview: React.FC<EntityPreviewProps> = (props) => (
  <LiveEntityPreview key={props.entity.kind} {...props} />
);

export interface EntityPreviewHoverCardProps extends EntityPreviewProps {
  children: ReactElement;
  placement?: TooltipPlacement;
  /** Gap between the trigger and the card, in pixels. */
  offset?: number;
  openDelayMs?: number;
  /** Keeps the card open while the pointer moves onto it, so its link out can be clicked. */
  interactive?: boolean;
  className?: string;
}

/** Shows an entity's preview while its trigger is hovered or focused. */
export const EntityPreviewHoverCard: React.FC<EntityPreviewHoverCardProps> = ({
  children,
  placement = "top-start",
  offset = 10,
  openDelayMs = 350,
  interactive = false,
  className,
  ...previewProps
}) => (
  <TooltipV2
    fadeOnly
    noArrow
    placement={placement}
    delay={{ open: openDelayMs, close: 75 }}
    offset={offset}
    persistOnHover={interactive}
    // A trigger can hold its own menu (a row's "…" actions); the card steps aside while it is up.
    suppressWhileDescendantExpanded
    className={cx(ENTITY_PREVIEW_SURFACE_CLASS, className)}
    content={<EntityPreview {...previewProps} />}
  >
    {children}
  </TooltipV2>
);

/** The floating card's frame, for hosts that show a preview in place rather than on hover. */
export const EntityPreviewSurface: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => (
  <div
    className={cx(
      "w-fit overflow-hidden rounded-cds-6 border-[1px] border-cds-surface-border",
      "bg-cds-surface-default shadow-cds-l",
      "dark:border-white/[0.04] dark:bg-cds-elevated-dark dark:shadow-cds-l-dark",
      className,
    )}
  >
    {children}
  </div>
);
